import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const number = (value) => Number(value || 0).toFixed(2);

const tableHeadSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 800,
  whiteSpace: "nowrap",
  py: 1.1,
  fontSize: 13
};

const cellSx = {
  fontSize: 12.5,
  color: "#0f172a",
  whiteSpace: "nowrap"
};

const SectionCard = ({ title, subtitle, icon, children }) => {
  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15,23,42,0.04)"
      }}
    >
      <Box
        sx={{
          p: 2.2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#fff"
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 17 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 0.35 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            bgcolor: "#eaf3ff",
            color: "#1565C0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
};

const StatCard = ({ title, value, icon, color = "#0f172a" }) => {
  return (
    <Paper sx={{ p: 2.3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography sx={{ color, fontSize: 28, fontWeight: 950, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            bgcolor: "#eaf3ff",
            color: "#1565C0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
};

const EmptyRow = ({ colSpan, text = "Sin registros para mostrar" }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 3, color: "#64748b", fontWeight: 700 }}>
      {text}
    </TableCell>
  </TableRow>
);

const SimplePagination = ({ count, page, setPage, rowsPerPage, setRowsPerPage }) => (
  <TablePagination
    component="div"
    count={count}
    page={page}
    onPageChange={(_e, value) => setPage(value)}
    rowsPerPage={rowsPerPage}
    onRowsPerPageChange={(e) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    }}
    rowsPerPageOptions={[5, 10, 20, 50]}
    labelRowsPerPage="Filas:"
  />
);

const paymentChipColor = (estado) => {
  const value = String(estado || "").toUpperCase();

  if (value === "PAGADA" || value === "PAGADO") return "success";
  if (value === "ABONADA" || value === "ABONADO") return "info";
  if (value === "ANULADA" || value === "ANULADO") return "error";

  return "warning";
};

const ProductStatsTab = ({ product, dashboardData }) => {
  const data = dashboardData || {};
  const stats = data.stats || {};
  const records = data.records || {};

  const purchases = Array.isArray(records.purchases) ? records.purchases : [];
  const sales = Array.isArray(records.sales) ? records.sales : [];
  const providers = Array.isArray(stats.proveedores) ? stats.proveedores : [];
  const clients = Array.isArray(stats.clientes) ? stats.clientes : [];

  const variants = useMemo(() => {
    if (Array.isArray(data.variants) && data.variants.length) return data.variants;
    if (Array.isArray(product?.variants)) return product.variants;
    return [];
  }, [data.variants, product?.variants]);

  const productStocks = useMemo(() => {
    return Array.isArray(product?.stocks_bodega) ? product.stocks_bodega : [];
  }, [product?.stocks_bodega]);

  const variantFactorMap = useMemo(() => {
    const map = new Map();

    variants.forEach((variant) => {
      const factor = Number(variant.factor_unidades || variant.conversion_quantity || 1) || 1;
      map.set(Number(variant.id), factor);
    });

    return map;
  }, [variants]);

  const stockByVariant = useMemo(() => {
    if (Array.isArray(data.stock_by_variant) && data.stock_by_variant.length) {
      return data.stock_by_variant;
    }

    return productStocks
      .filter((row) => row.variant_id)
      .map((row) => ({
        ...row,
        variant_value: row.variant_value || row.value || "",
        variant_sku: row.variant_sku || row.sku || ""
      }));
  }, [data.stock_by_variant, productStocks]);

  const stockByBodega = useMemo(() => {
    const dashboardRows = Array.isArray(data.stock_by_bodega) ? data.stock_by_bodega : [];

    if (dashboardRows.length) {
      return dashboardRows;
    }

    const grouped = new Map();

    productStocks.forEach((row) => {
      const key = String(row.bodega_id || row.bodega_codigo || row.bodega_nombre || "sin-bodega");
      const factor = row.variant_id ? variantFactorMap.get(Number(row.variant_id)) || 1 : 1;
      const cantidad = Number(row.cantidad || 0);
      const cantidadEquivalente = cantidad * factor;

      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          bodega_id: row.bodega_id,
          bodega_codigo: row.bodega_codigo,
          bodega_nombre: row.bodega_nombre,
          cantidad: 0,
          stock_minimo: row.stock_minimo ?? product?.stock_minimo ?? 0,
          activo: row.activo
        });
      }

      const current = grouped.get(key);
      current.cantidad += cantidadEquivalente;
    });

    return Array.from(grouped.values());
  }, [data.stock_by_bodega, productStocks, product?.stock_minimo, variantFactorMap]);

  const [purchasePage, setPurchasePage] = useState(0);
  const [purchaseRows, setPurchaseRows] = useState(5);
  const [salePage, setSalePage] = useState(0);
  const [saleRows, setSaleRows] = useState(5);
  const [providerPage, setProviderPage] = useState(0);
  const [providerRows, setProviderRows] = useState(5);
  const [clientPage, setClientPage] = useState(0);
  const [clientRows, setClientRows] = useState(5);

  const purchaseSlice = useMemo(
    () => purchases.slice(purchasePage * purchaseRows, purchasePage * purchaseRows + purchaseRows),
    [purchases, purchasePage, purchaseRows]
  );

  const saleSlice = useMemo(
    () => sales.slice(salePage * saleRows, salePage * saleRows + saleRows),
    [sales, salePage, saleRows]
  );

  const providerSlice = useMemo(
    () => providers.slice(providerPage * providerRows, providerPage * providerRows + providerRows),
    [providers, providerPage, providerRows]
  );

  const clientSlice = useMemo(
    () => clients.slice(clientPage * clientRows, clientPage * clientRows + clientRows),
    [clients, clientPage, clientRows]
  );

  const totalStock = Number(product?.stock ?? product?.stock_real ?? stats.stock_total ?? 0);
  const totalBought = Number(stats.cantidad_comprada || 0);
  const totalSold = Number(stats.cantidad_vendida || 0);
  const netSales = Number(stats.venta_total_neta || 0);
  const paidTotal = Number(stats.pagado_total || 0);
  const receivableTotal = Number(stats.por_cobrar_total ?? stats.saldo_total ?? 0);

  const variantModeLabel = (row) => {
    const mode = row.stock_mode || "OWN";
    if (mode === "DERIVED_PRODUCT") return "Derivada del producto";
    if (mode === "DERIVED_VARIANT") return "Derivada de variante";
    return "Stock propio";
  };

  const variantModeColor = (row) => {
    const mode = row.stock_mode || "OWN";
    if (mode === "DERIVED_PRODUCT") return "info";
    if (mode === "DERIVED_VARIANT") return "warning";
    return "success";
  };

  const variantModeVariant = (row) => {
    const mode = row.stock_mode || "OWN";
    return mode === "OWN" ? "outlined" : "filled";
  };

  return (
    <Box>
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={2.4}>
          <StatCard
            title="Stock actual"
            value={number(totalStock)}
            icon={<Inventory2OutlinedIcon />}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <StatCard
            title="Unidades compradas"
            value={number(totalBought)}
            icon={<ShoppingCartOutlinedIcon />}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <StatCard
            title="Unidades vendidas"
            value={number(totalSold)}
            icon={<PointOfSaleOutlinedIcon />}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <StatCard
            title="Venta neta"
            value={money(netSales)}
            icon={<PaidOutlinedIcon />}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <StatCard
            title="Por cobrar"
            value={money(receivableTotal)}
            icon={<AccountBalanceWalletOutlinedIcon />}
            color={receivableTotal > 0 ? "#d97706" : "#0f172a"}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <StatCard
            title="Pagado"
            value={money(paidTotal)}
            icon={<PaidOutlinedIcon />}
            color="#15803d"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Stock por bodega"
            subtitle="Existencias equivalentes actuales por bodega"
            icon={<Inventory2OutlinedIcon />}
          >
            <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Bodega</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Cantidad</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Mínimo</TableCell>
                    <TableCell sx={tableHeadSx} align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockByBodega.length === 0 ? (
                    <EmptyRow colSpan={4} />
                  ) : (
                    stockByBodega.map((row) => {
                      const bajo = Number(row.cantidad || 0) <= Number(row.stock_minimo || product?.stock_minimo || 0);

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={cellSx}>
                            <Stack spacing={0.2}>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                                {row.bodega_nombre || "-"}
                              </Typography>
                              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                {row.bodega_codigo || ""}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={cellSx} align="right">
                            {number(row.cantidad)}
                          </TableCell>
                          <TableCell sx={cellSx} align="right">
                            {number(row.stock_minimo)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={bajo ? "Bajo" : "Normal"}
                              color={bajo ? "error" : "success"}
                              size="small"
                              sx={{ fontWeight: 800, borderRadius: 2 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Variantes"
            subtitle="Presentaciones, colores, tamaños, combos o conversiones del producto"
            icon={<LocalOfferOutlinedIcon />}
          >
            <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Variante</TableCell>
                    <TableCell sx={tableHeadSx}>SKU</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Contenido</TableCell>
                    <TableCell sx={tableHeadSx} align="center">Inventario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {variants.length === 0 ? (
                    <EmptyRow colSpan={4} text="Este producto no tiene variantes" />
                  ) : (
                    variants.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={cellSx}>
                          <Stack spacing={0.2}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                              {row.value || "-"}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                              {row.variant_type || ""}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={cellSx}>{row.sku || "-"}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          {number(row.conversion_quantity || row.factor_unidades || 1)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={variantModeLabel(row)}
                            size="small"
                            color={variantModeColor(row)}
                            variant={variantModeVariant(row)}
                            sx={{ fontWeight: 700, borderRadius: 2 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>

        {stockByVariant.length > 0 && (
          <Grid item xs={12}>
            <SectionCard
              title="Stock por variante"
              subtitle="Existencias físicas separadas por variante y bodega"
              icon={<Inventory2OutlinedIcon />}
            >
              <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadSx}>Variante</TableCell>
                      <TableCell sx={tableHeadSx}>Bodega</TableCell>
                      <TableCell sx={tableHeadSx} align="right">Cantidad</TableCell>
                      <TableCell sx={tableHeadSx} align="right">Mínimo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockByVariant.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={cellSx}>
                          {row.variant_value || row.variant_sku || "-"}
                        </TableCell>
                        <TableCell sx={cellSx}>
                          {row.bodega_codigo ? `${row.bodega_codigo} - ${row.bodega_nombre}` : row.bodega_nombre || "-"}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {number(row.cantidad)}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {number(row.stock_minimo)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>
          </Grid>
        )}

        <Grid item xs={12}>
          <SectionCard
            title="Historial de compras"
            subtitle="Movimiento documental y unidades reales ingresadas al inventario"
            icon={<ShoppingCartOutlinedIcon />}
          >
            <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Fecha</TableCell>
                    <TableCell sx={tableHeadSx}>Proveedor</TableCell>
                    <TableCell sx={tableHeadSx}>Documento</TableCell>
                    <TableCell sx={tableHeadSx}>Bodega</TableCell>
                    <TableCell sx={tableHeadSx}>Variante</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Movimiento</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Unid. reales</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Costo unit.</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Total</TableCell>
                    <TableCell sx={tableHeadSx} align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseSlice.length === 0 ? (
                    <EmptyRow colSpan={10} text="No hay compras registradas para este producto" />
                  ) : (
                    purchaseSlice.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={cellSx}>{row.fecha_emision || "-"}</TableCell>
                        <TableCell sx={cellSx}>{row.proveedor || "-"}</TableCell>
                        <TableCell sx={cellSx}>{row.numero_documento || row.secuencia_inventario || "-"}</TableCell>
                        <TableCell sx={cellSx}>
                          {row.bodega_codigo ? `${row.bodega_nombre}` : row.bodega_nombre || "-"}
                        </TableCell>
                        <TableCell sx={cellSx}>{row.variant_value || row.sku || "-"}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          {row.movimiento_label || number(row.cantidad_documento)}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {number(row.cantidad_real ?? row.cantidad)}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.costo_unitario)}</TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.total)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.estado || "-"}
                            size="small"
                            color={paymentChipColor(row.estado)}
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <SimplePagination
              count={purchases.length}
              page={purchasePage}
              setPage={setPurchasePage}
              rowsPerPage={purchaseRows}
              setRowsPerPage={setPurchaseRows}
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title="Historial de ventas y notas de crédito"
            subtitle="Movimiento documental, valores pagados y saldos pendientes del producto"
            icon={<PointOfSaleOutlinedIcon />}
          >
            <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1420 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Fecha</TableCell>
                    <TableCell sx={tableHeadSx}>Cliente</TableCell>
                    <TableCell sx={tableHeadSx}>Documento</TableCell>
                    <TableCell sx={tableHeadSx}>Bodega</TableCell>
                    <TableCell sx={tableHeadSx}>Variante</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Movimiento</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Unid. reales</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Precio unit.</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Total</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Pagado</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Por cobrar</TableCell>
                    <TableCell sx={tableHeadSx} align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {saleSlice.length === 0 ? (
                    <EmptyRow colSpan={12} text="No hay ventas registradas para este producto" />
                  ) : (
                    saleSlice.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={cellSx}>{row.fecha_emision || "-"}</TableCell>
                        <TableCell sx={cellSx}>{row.cliente || "-"}</TableCell>
                        <TableCell sx={cellSx}>{row.numero_documento || "-"}</TableCell>
                        <TableCell sx={cellSx}>
                          {row.bodega_codigo ? `${row.bodega_nombre}` : row.bodega_nombre || "-"}
                        </TableCell>
                        <TableCell sx={cellSx}>{row.variant_value || row.sku || "-"}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          {row.movimiento_label || number(row.cantidad_documento)}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {number(row.cantidad_real ?? row.cantidad)}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.precio_unitario)}</TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.total)}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#15803d" }}>
                            {money(row.pagado_producto)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 800,
                              color: Number(row.saldo_producto || 0) > 0 ? "#d97706" : "#0f172a"
                            }}
                          >
                            {money(row.saldo_producto)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.estado || "-"}
                            size="small"
                            color={paymentChipColor(row.estado)}
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <SimplePagination
              count={sales.length}
              page={salePage}
              setPage={setSalePage}
              rowsPerPage={saleRows}
              setRowsPerPage={setSaleRows}
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Proveedores"
            subtitle="Proveedores que han vendido este producto"
            icon={<StorefrontOutlinedIcon />}
          >
            <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Proveedor</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Unid.</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Prom.</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providerSlice.length === 0 ? (
                    <EmptyRow colSpan={4} />
                  ) : (
                    providerSlice.map((row, index) => (
                      <TableRow key={`${row.proveedor_id}-${index}`} hover>
                        <TableCell sx={cellSx}>{row.proveedor || "-"}</TableCell>
                        <TableCell sx={cellSx} align="right">{number(row.cantidad)}</TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.precio_promedio)}</TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.total)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <SimplePagination
              count={providers.length}
              page={providerPage}
              setPage={setProviderPage}
              rowsPerPage={providerRows}
              setRowsPerPage={setProviderRows}
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Clientes"
            subtitle="Clientes que han comprado este producto"
            icon={<GroupsOutlinedIcon />}
          >
            <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Cliente</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Unid.</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Prom.</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Total</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Pagado</TableCell>
                    <TableCell sx={tableHeadSx} align="right">Por cobrar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientSlice.length === 0 ? (
                    <EmptyRow colSpan={6} />
                  ) : (
                    clientSlice.map((row, index) => (
                      <TableRow key={`${row.identificacion}-${index}`} hover>
                        <TableCell sx={cellSx}>
                          <Stack spacing={0.2}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                              {row.cliente || "-"}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                              {row.identificacion || ""}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={cellSx} align="right">{number(row.cantidad)}</TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.precio_promedio)}</TableCell>
                        <TableCell sx={cellSx} align="right">{money(row.total)}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#15803d" }}>
                            {money(row.pagado)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 800,
                              color: Number(row.saldo || 0) > 0 ? "#d97706" : "#0f172a"
                            }}
                          >
                            {money(row.saldo)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <SimplePagination
              count={clients.length}
              page={clientPage}
              setPage={setClientPage}
              rowsPerPage={clientRows}
              setRowsPerPage={setClientRows}
            />
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductStatsTab;